import React, { useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { melonPrismTheme } from "./codeTheme";

function CodeBlock({ language, code }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // clipboard API rejected (insecure context, etc.)
      const ta = document.createElement("textarea");
      ta.value = code;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch {}
      document.body.removeChild(ta);
    }
  };

  return (
    <div className="relative my-6 group">
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? "Copied to clipboard" : "Copy code to clipboard"}
        className={`absolute top-2 right-2 z-10 font-arcade text-xs uppercase tracking-wide px-2 py-1 border-2 transition-colors duration-150 ${
          copied
            ? "bg-[#15803D] text-white border-[#15803D]"
            : "bg-white text-[#15803D] border-[#D4D4D4] hover:border-[#15803D]"
        }`}
      >
        {copied ? "COPIED!" : "COPY"}
      </button>
      <SyntaxHighlighter
        language={language}
        style={melonPrismTheme}
        PreTag="div"
        customStyle={{ margin: 0 }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

export const mdComponents = {
  code({ inline, className, children, ...rest }) {
    const match = /language-(\w+)/.exec(className || "");
    const isBlock = !inline && match;
    if (!isBlock) {
      return (
        <code
          className="font-terminal text-[0.85em] bg-[#E8E8E8] border border-[#D4D4D4] px-1.5 py-0.5 text-[#15803D]"
          {...rest}
        >
          {children}
        </code>
      );
    }
    const code = String(children).replace(/\n$/, "");
    return <CodeBlock language={match[1]} code={code} />;
  },
  pre({ children }) {
    return <>{children}</>;
  },
  img({ src, alt, ...rest }) {
    return (
      <figure className="my-8">
        <div className="border-2 border-[#D4D4D4] bg-white p-4" style={{ boxShadow: "4px 4px 0px 0px #15803D" }}>
          <img
            src={src}
            alt={alt}
            className="w-full block"
            style={{ imageRendering: "pixelated" }}
            loading="lazy"
            {...rest}
          />
        </div>
        {alt && (
          <figcaption className="font-arcade text-base text-[#0A0A0A]/55 mt-3 text-center uppercase tracking-wide">
            &gt; {alt}
          </figcaption>
        )}
      </figure>
    );
  },
  a({ href, children, ...rest }) {
    const isExternal = href && /^https?:\/\//i.test(href);
    return (
      <a
        href={href}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
        {...rest}
      >
        {children}
      </a>
    );
  },
  table({ children }) {
    return (
      <div className="my-6 overflow-x-auto border-2 border-[#D4D4D4]" style={{ boxShadow: "4px 4px 0px 0px #15803D" }}>
        <table className="w-full">{children}</table>
      </div>
    );
  },
};
